'use client'

import { motion } from 'motion/react'
import { useTranslations } from 'next-intl'
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useMeasure } from 'react-use'
import { ExpandToggleButton } from '@/components/expand-toggle-button'
import { useRestaurantDetailContext } from '@/features/restaurants/restaurant-detail/restaurant-detail-context'
import { RestaurantDetailMenuCategoryBar } from '@/features/restaurants/restaurant-detail/restaurant-detail-menu/restaurant-detail-menu-category-bar'
import { RestaurantDetailMenuGroup } from '@/features/restaurants/restaurant-detail/restaurant-detail-menu/restaurant-detail-menu-group'
import { RestaurantDetailSection } from '@/features/restaurants/restaurant-detail/restaurant-detail-section'
import { RestaurantDetailSectionId } from '@/features/restaurants/restaurant-detail/utils/restaurant-detail-helpers'
import { throttle } from '@/utils/functions/throttle'

/** Roughly the first category plus a hint of the next one */
const COLLAPSED_MAX_HEIGHT = 480

/** Below this many categories the sticky chip bar is not worth its space */
const MIN_CATEGORIES_FOR_BAR = 3

// Stacked sticky heights above a scrolled-to category heading: the section tab
// bar (plus the fixed 64px site navigation on mobile) and the chip bar itself
const getCategoryScrollOffset = () =>
    typeof window !== 'undefined' && window.innerWidth < 768 ? 182 : 118

export function RestaurantDetailMenu() {
    const t = useTranslations()

    const { restaurant } = useRestaurantDetailContext()

    const [isExpanded, setIsExpanded] = useState(false)
    const [activeCategoryUuid, setActiveCategoryUuid] = useState<string | null>(null)

    const [containerRef, { height: containerHeight }] = useMeasure<HTMLDivElement>()

    const groupRefs = useRef<Record<string, HTMLDivElement | null>>({})

    // While a chip-triggered smooth scroll is running, the spy must not select
    // the categories being scrolled past
    const spyIgnoreUntilUuid = useRef<string | null>(null)

    const menuGroups = useMemo(
        () => restaurant?.menuGroups?.filter((menuGroup) => menuGroup.menuItems?.length) ?? [],
        [restaurant?.menuGroups]
    )

    const scrollToCategory = useCallback((uuid: string) => {
        const element = groupRefs.current[uuid]

        if (!element) return

        spyIgnoreUntilUuid.current = uuid
        setActiveCategoryUuid(uuid)

        window.scrollTo({
            behavior: 'smooth',
            top: element.getBoundingClientRect().top + window.scrollY - getCategoryScrollOffset(),
        })
    }, [])

    const onSelectCategory = useCallback(
        (uuid: string) => {
            if (!isExpanded) {
                setIsExpanded(true)

                // Wait for the expand height animation so the target position is final
                setTimeout(() => scrollToCategory(uuid), 250)

                return
            }

            scrollToCategory(uuid)
        },
        [isExpanded, scrollToCategory]
    )

    const throttledSpy = useMemo(
        () =>
            throttle(() => {
                const offset = getCategoryScrollOffset() + 8

                let current: string | null = null

                for (const menuGroup of menuGroups) {
                    const element = groupRefs.current[menuGroup.uuid]

                    if (!element) continue

                    if (element.getBoundingClientRect().top <= offset) {
                        current = menuGroup.uuid
                    }
                }

                if (spyIgnoreUntilUuid.current) {
                    if (current !== spyIgnoreUntilUuid.current) return

                    spyIgnoreUntilUuid.current = null
                }

                setActiveCategoryUuid(current ?? menuGroups[0]?.uuid ?? null)
            }, 150),
        [menuGroups]
    )

    useLayoutEffect(() => {
        throttledSpy()

        window.addEventListener('scroll', throttledSpy)

        return () => window.removeEventListener('scroll', throttledSpy)
    }, [throttledSpy])

    if (!restaurant || !menuGroups.length) return null

    const needsCollapse = containerHeight > COLLAPSED_MAX_HEIGHT

    const visibleHeight =
        isExpanded || !needsCollapse ? containerHeight || 'auto' : COLLAPSED_MAX_HEIGHT

    const showCategoryBar = menuGroups.length >= MIN_CATEGORIES_FOR_BAR

    return (
        <RestaurantDetailSection
            id={RestaurantDetailSectionId.MENU}
            title={'restaurants.detail.menu.title'}
            className="mb-14"
        >
            {showCategoryBar && (
                <RestaurantDetailMenuCategoryBar
                    categories={menuGroups.map(({ uuid, name }) => ({ uuid, name }))}
                    activeUuid={activeCategoryUuid ?? menuGroups[0]?.uuid ?? null}
                    onSelect={onSelectCategory}
                />
            )}

            <motion.div
                initial={false}
                animate={{ height: visibleHeight }}
                transition={{ height: { type: 'tween', duration: 0.2 } }}
                className="overflow-hidden relative"
            >
                <div ref={containerRef} className="flex flex-col gap-y-10">
                    {menuGroups.map((menuGroup) => (
                        <div
                            key={'restaurant-detail-menu' + menuGroup.uuid}
                            ref={(element) => {
                                groupRefs.current[menuGroup.uuid] = element
                            }}
                        >
                            <RestaurantDetailMenuGroup
                                currencySymbol={restaurant.city?.country?.currency?.symbol ?? '€'}
                                menuGroup={menuGroup}
                            />
                        </div>
                    ))}
                </div>

                {/* Short cutoff hint, not a veil over the content */}
                {!isExpanded && needsCollapse && (
                    <div
                        className="absolute inset-x-0 bottom-0 h-28 pointer-events-none bg-gradient-to-t
                            from-bg-neutral-base to-transparent"
                    />
                )}
            </motion.div>

            {needsCollapse && (
                <ExpandToggleButton
                    isExpanded={isExpanded}
                    onClick={() => setIsExpanded((prev) => !prev)}
                >
                    {isExpanded
                        ? t('restaurants.detail.menu.showLess')
                        : t('restaurants.detail.menu.showAll')}
                </ExpandToggleButton>
            )}
        </RestaurantDetailSection>
    )
}
